import { createSelector } from 'reselect';

// Constants
import {
  DEFAULT_UNASSIGNED_TO_RETRIEVE_COUNT,
  INITIAL_ALERT,
} from 'app/modules/alerts/constants';

// Selectors
import {
  selectHasReadAssignmentsPermissions,
  selectHasReadAgentsPermissions,
  selectHasEditAlertsPermission,
} from 'app/modules/session/selectors';
import {
  selectAlertEnabledFeatures,
  selectLumosConfig,
} from 'app/modules/orgSettings/selectors';

import { AlertsActionTypes, AlertsState } from 'app/modules/alerts/models';

export const selectAlerts = (state: RootState): AlertsState['alerts'] => {
  return state.alerts.alerts || [];
};

export const selectAlert = (state: RootState) => {
  return state?.alerts?.alert || INITIAL_ALERT;
};

export const selectAlertHistogram = (state: RootState) =>
  state.alerts.histogram;

export const selectAlertEntities = createSelector(
  selectAlert,
  ({ entities }) => entities || [],
);

export const selectIsIntuitAlert = createSelector(
  selectAlertEnabledFeatures,
  (enabledFeatures) => {
    // These features are unique to Intuit
    return (
      enabledFeatures.includes('ASSOCIATED_USERS') &&
      enabledFeatures.includes('ASSOCIATED_BUSINESSES')
    );
  },
);

// Loading
export const selectAlertActionEventsLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT_ACTION_EVENTS]);

export const selectRetrieveAlertEntitiesLoading = (state: RootState) => {
  return Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT_ENTITIES]);
};

export const selectAlertTransactionsLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT_TRANSACTIONS]);

export const selectAlertEntitiesLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT_ENTITIES]);

export const selectAlertLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT]);

export const selectAlertComponentStatusLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.CHANGE_ALERT_COMPONENT_STATUS]);

export const selectCreateAlertLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.CREATE_ALERT]);

export const selectEditAlertLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.EDIT_ALERT]);

export const selectAlertActionEvents = createSelector(
  selectAlert,
  (alert) =>
    alert.action_events || {
      count: 0,
      events: [],
    },
);

export const selectAlertAssociatedEntities = createSelector(
  selectAlert,
  ({ entities }) => entities || [],
);

export const selectAlertHistogramLoading = (state: RootState) =>
  Boolean(state.loading[AlertsActionTypes.RETRIEVE_ALERT_HISTOGRAM]);

export const selectHasValidAlert = createSelector(
  selectAlert,
  selectAlertLoading,
  ({ id }, alertLoading) => id !== -1 && !alertLoading,
);

export const selectNumUnassignedToRetrieve = createSelector(
  selectLumosConfig,
  (lumosConfig): number =>
    lumosConfig.unassigned_to_retrieve_count ||
    DEFAULT_UNASSIGNED_TO_RETRIEVE_COUNT,
);

export const selectHasReassignAlertPermission = createSelector(
  selectHasReadAgentsPermissions,
  selectHasEditAlertsPermission,
  (hasReadAgentsPermission, hasEditAlertsPermission) => {
    return hasReadAgentsPermission && hasEditAlertsPermission;
  },
);

export const selectHasRequeueAlertPermission = createSelector(
  selectHasReadAssignmentsPermissions,
  selectHasEditAlertsPermission,
  (hasReadAssignmentsPermission, hasEditAlertsPermission) => {
    return hasReadAssignmentsPermission && hasEditAlertsPermission;
  },
);
